import { useId } from 'react'

import { useApiClient } from '@/api/ApiClientProvider'
import type { FunnelStageKey, PeriodInfo } from '@/api/types'
import { SectionFrame } from '@/app/pages'
import type { RouteDef } from '@/app/routes'
import { Card, EmptyState, LoadingBlock } from '@/design'
import { useDrillDown, useOverviewQuery } from '@/features/overview/hooks'
import { usePageFreshness } from '@/lib/freshness'
import { dayCount, DIMENSION_LABELS, DIMENSIONS, formatWindow, type GlobalFilters } from '@/lib/filters'
import { useGlobalFilters } from '@/lib/useGlobalFilters'

import { Coverage } from './Coverage'
import { hasNoMeasuredSessions } from './figures'
import { FunnelDefinitions, FunnelKpis } from './FunnelKpis'
import { FunnelTable } from './FunnelTable'
import { useFunnelStage } from './params'
import { RecoveryTile } from './RecoveryTile'
import { StageDetail } from './StageDetail'
import { useStageFocus } from './useStageFocus'
import './funnel.css'

/**
 * Analytics › Funnel (card 3d9c00e6): where visitors drop out between landing and
 * purchase. Six headline figures, the seven stages with every figure
 * `GET /overview/funnel` gives, and one stage's detail at a time (`?stage=`).
 * The recoverable checkouts themselves are the Abandonment tab's.
 */
export function FunnelPage({ route }: { route: RouteDef }) {
  return (
    <SectionFrame route={route}>
      <div className="ci-stack">
        <TrafficSelector />
        <FunnelBody />
      </div>
    </SectionFrame>
  )
}

/**
 * The design's "All traffic" switch. The funnel route takes no channel or
 * source, so splitting paid from organic would be invented: the control is
 * shown, disabled, with the reason.
 */
function TrafficSelector() {
  const hintId = useId()
  return (
    <div className="fun-controls">
      <label className="ci-field ci-field--inline">
        <span>Traffic</span>
        <select disabled aria-describedby={hintId} value="all">
          <option value="all">All traffic</option>
        </select>
      </label>
      <p id={hintId} className="fun-note">
        Not live yet — needs a channel filter on /overview/funnel. Every measured session is counted.
      </p>
    </div>
  )
}

/** The global filters the live funnel cannot apply: it is scoped by dates, website and environment only. */
function ignoredDimensions(filters: GlobalFilters): string[] {
  return DIMENSIONS.filter((dimension) => dimension !== 'website' && filters.dimensions[dimension]).map(
    (dimension) => DIMENSION_LABELS[dimension],
  )
}

function previousWindow(period: PeriodInfo | undefined): string | null {
  if (!period?.previousFrom || !period.previousTo) return null
  return formatWindow({ from: period.previousFrom, to: period.previousTo, days: dayCount(period.previousFrom, period.previousTo) })
}

function FunnelBody() {
  const funnel = useOverviewQuery('funnel', (client, filters) => client.funnel(filters))
  const client = useApiClient()
  const { filters } = useGlobalFilters()
  const [stage, setStage] = useFunnelStage()
  const href = useDrillDown()
  usePageFreshness(funnel.data?.meta)
  const focus = useStageFocus(stage)
  // The one way a stage opens or closes: choosing the open stage again (or its Close) closes it and returns focus to it.
  const toggleStage = (key: FunnelStageKey) => {
    const next = key === stage ? undefined : key
    focus.after(next ? 'detail' : key)
    setStage(next)
  }

  if (funnel.isPending) return <LoadingBlock rows={6} label="Loading the funnel" />
  if (!funnel.data) return <EmptyState variant="outage" announce />

  const { meta, data: stages } = funnel.data
  if (hasNoMeasuredSessions(stages)) {
    return (
      <Card>
        <EmptyState variant="no-data" title="No measured sessions in this period" description={meta.coverage?.note} />
      </Card>
    )
  }

  const compareLabel = meta.period?.compareLabel ?? null
  const ignored = client.kind === 'http' ? ignoredDimensions(filters) : []
  const abandonmentHref = href({ path: '/analytics/abandonment' })
  return (
    <>
      {ignored.length > 0 ? (
        <p className="fun-note" role="note">
          The funnel is not split by {ignored.join(', ').toLowerCase()} yet: these figures cover every measured session
          for the dates and website chosen.
        </p>
      ) : null}
      <FunnelKpis stages={stages} compareLabel={compareLabel} />
      <FunnelDefinitions />
      <Card
        title="Conversion funnel"
        subtitle={`Measured sessions${meta.period ? ` · ${meta.period.label}` : ''}. Choose a stage for its detail.`}
        busy={funnel.isFetching}
      >
        <FunnelTable
          stages={stages}
          selected={stage}
          onSelect={toggleStage}
        />
        {meta.coverage ? <Coverage coverage={meta.coverage} /> : null}
      </Card>
      {stage ? (
        <StageDetail
          ref={focus.detail}
          stages={stages}
          stage={stage}
          previousWindow={previousWindow(meta.period)}
          abandonmentHref={abandonmentHref}
          onClose={() => toggleStage(stage)}
        />
      ) : null}
      <RecoveryTile environments={filters.environments} href={abandonmentHref} />
    </>
  )
}
